import {
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    EventEmitter,
    HostBinding,
    Input,
    OnInit,
    Output,
    Signal,
    computed,
    inject,
    input,
    signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { RewardTargetType } from '@bpos/v1/sports-promo/tokens';
import { CrmConfig, EpcotConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { interpolateDoubleBracesString } from '@frontend/sports/common/core/utils/string';
import { CrmTokensMessages } from '@frontend/sports/types/components/sitecore';
import { Store } from '@ngrx/store';

import { RewardTokenType } from '../../../../../lib/tokens-base/token-base.models';
import { UiManagerActions } from '../../../../../lib/ui-manager/ui-manager.actions';
import { IUiRootState } from '../../../../../lib/ui-manager/ui-manager.state';
import { UiStates } from '../../../../../lib/ui-manager/ui-states.enum';
import { FreeBetToken, IRewardToken } from '../../reward-tokens.model';
import { selectRewardTokensList } from '../../selectors';
import { getAcquisitionRewardToken, getNonAccaTokens, isAccaBoostToken, isFreebetToken } from '../../services/reward-tokens.utils';

interface SelectedTokenModel {
    title: string;
    icon?: string;
    altIcon?: string;
    className: string;
}

@Component({
    selector: 'bs-reward-tokens-selector',
    templateUrl: './reward-tokens-selector.component.html',
    styleUrls: ['./reward-tokens-selector.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class RewardTokensSelectorComponent implements OnInit {
    @HostBinding('class') class = 'reward-tokens-module';

    selectedToken = input<IRewardToken>();
    hasAcquisitionOnboarding = input<boolean>();

    @Input() infoEnabled = false;
    @Input() isTokenInvalid: boolean;

    @Output() selectorClick = new EventEmitter<void>();
    @Output() infoClick = new EventEmitter<void>();

    private readonly translationMessages: CrmTokensMessages;
    readonly selectorMessages: {
        apply: string;
        edit: string;
        rewardApplied: string;
    };

    readonly isEpcotEnabled: boolean;
    private availableRewards: IRewardToken[];
    displayAddWelcomeOfferTooltip = signal(false);

    readonly selectedTokenModel: Signal<SelectedTokenModel | null> = computed(() => this.getSelectedTokenModel(this.selectedToken()));
    readonly shouldDisplayApplyButton: Signal<boolean> = computed(() => getNonAccaTokens(this.availableRewards).length > 0);
    readonly #destroyRef = inject(DestroyRef);

    constructor(
        private store: Store<IUiRootState>,
        private tokensConfig: CrmConfig,
        private sitecore: Sitecore,
        epcotConfig: EpcotConfig,
    ) {
        const crmTokens = sitecore.crmTokens;

        this.translationMessages = crmTokens.messages;
        this.selectorMessages = {
            apply: crmTokens.linearRewardTokens.TokensSelectorApply,
            edit: crmTokens.linearRewardTokens.TokensSelectorEdit,
            rewardApplied: crmTokens.linearRewardTokens.RewardApplied,
        };

        this.isEpcotEnabled = epcotConfig.isEnabled;
    }

    ngOnInit(): void {
        this.store
            .select(selectRewardTokensList)
            .pipe(takeUntilDestroyed(this.#destroyRef))
            .subscribe((tokens) => {
                this.availableRewards = tokens;
            });
        this.displayAddWelcomeOfferTooltip.set(
            !!getAcquisitionRewardToken(this.availableRewards) &&
                this.tokensConfig.tokens.acquisitionRewardDetails.isWelcomeOfferTokenAllowed &&
                !this.tokensConfig.tokens.acquisitionRewardDetails.isAutoApplyEnabled,
        );
    }

    hideAddWelcomeOfferTooltip(): void {
        this.displayAddWelcomeOfferTooltip.set(false);
    }

    onClick(event: MouseEvent): void {
        this.store.dispatch(UiManagerActions.removeState({ state: UiStates.MarketSubTypeTooltipShown }));

        event.preventDefault();
        event.stopPropagation();

        this.selectorClick.next();
    }

    displayInfo(event: Event): void {
        event.preventDefault();
        event.stopPropagation();

        this.infoClick.next();
    }

    get freeBetAmount(): number | null {
        return isFreebetToken(this.selectedToken()) ? (<FreeBetToken>this.selectedToken()).amount : null;
    }

    get tokenIconClass(): string {
        return this.isTokenInvalid ? 'linear-rewards-selector__icon--invalid theme-warning' : 'linear-rewards-selector__icon--valid theme-success-i';
    }

    getTokenTitle(tokenTitle: string): string {
        return interpolateDoubleBracesString(this.selectorMessages.rewardApplied, { tokenName: tokenTitle });
    }

    private getSelectedTokenModel(selectedToken?: IRewardToken | null): SelectedTokenModel | null {
        if (!selectedToken || isAccaBoostToken(selectedToken)) {
            return null;
        }

        const tokenCustomTag =
            selectedToken.rewardTargetType === RewardTargetType.WelcomeOffer ? this.sitecore.crmTokens.messages.WelcomeOfferTag : undefined;

        switch (selectedToken.rewardTokenType) {
            case RewardTokenType.OddsBoost: {
                return {
                    title: tokenCustomTag ?? this.translationMessages.OddsBoost,
                    icon: this.tokensConfig.tokens.icons.oddsBoost,
                    className: RewardTokenType.OddsBoost.toLowerCase(),
                };
            }
            case RewardTokenType.RiskFreeBet: {
                return {
                    title: tokenCustomTag ?? this.translationMessages.RiskFree,
                    icon: this.tokensConfig.tokens.icons.riskFree,
                    className: RewardTokenType.RiskFreeBet.toLowerCase(),
                };
            }
            case RewardTokenType.FreeBet: {
                return {
                    title: this.translationMessages.Freebet,
                    icon: this.tokensConfig.tokens.icons.freeBet,
                    className: RewardTokenType.FreeBet.toLowerCase(),
                };
            }
            case RewardTokenType.BetAndGet: {
                return {
                    title: tokenCustomTag ?? this.translationMessages.BetAndGet,
                    icon: this.tokensConfig.tokens.icons.betAndGet,
                    className: RewardTokenType.BetAndGet.toLowerCase(),
                };
            }
        }

        return null;
    }
}
